import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Layers,
  List,
  Network,
  Globe,
  Building2,
  Puzzle,
  BookOpen,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Lightbulb,
  Link2,
  ArrowRight,
  Sparkles,
  GitBranch,
  Zap,
  Route,
  ShieldCheck,
  Brain,
  Tag,
  Share2,
  ArrowRightLeft,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ALL_CLASSES, getClassesByDomain, getGroupsForDomain, getClassesByGroup, CROSS_LAYER_RELATIONSHIPS, TRACEABILITY_CHAINS, SWRL_RULES } from '@/lib/ontology/schema'
import type { SWRLRule, EntityGroup } from '@/lib/ontology/schema'
import { getLocalName } from '@/lib/ontology/namespaces'
import { query } from '@/lib/api/sparql-adapter'
import type { OntologyClass, OntologyDomain } from '@/types/ontology'

type ViewMode = 'diagram' | 'tree'

const DOMAIN_CONFIG: Record<OntologyDomain, { color: string; bg: string; lightBg: string; border: string; label: string; icon: typeof Globe; question: string }> = {
  core: {
    color: 'var(--color-core)',
    bg: 'var(--color-core)',
    lightBg: 'var(--color-warm-100)',
    border: 'var(--color-border)',
    label: 'Core Layer',
    icon: Layers,
    question: 'WHERE does knowledge come from?',
  },
  market: {
    color: 'var(--color-market-700)',
    bg: 'var(--color-market)',
    lightBg: 'var(--color-market-50)',
    border: 'var(--color-market-100)',
    label: 'Market Layer',
    icon: Globe,
    question: 'WHO are you building for?',
  },
  business: {
    color: 'var(--color-business-700)',
    bg: 'var(--color-business)',
    lightBg: 'var(--color-business-50)',
    border: 'var(--color-business-100)',
    label: 'Business Layer',
    icon: Building2,
    question: 'HOW will you create value?',
  },
  solution: {
    color: 'var(--color-solution-800)',
    bg: 'var(--color-solution)',
    lightBg: 'var(--color-solution-50)',
    border: 'var(--color-solution-200)',
    label: 'Solution Layer',
    icon: Puzzle,
    question: 'WHAT will you build?',
  },
}

export function OntologyExplorer() {
  const [viewMode, setViewMode] = useState<ViewMode>('diagram')
  const [selectedClass, setSelectedClass] = useState<OntologyClass | null>(null)
  const [showEducational, setShowEducational] = useState(true)

  // Get instance counts for all classes
  const { data: instanceCounts = {} } = useQuery({
    queryKey: ['instanceCounts'],
    queryFn: async () => {
      const result = await query(`
        SELECT ?type (COUNT(?instance) as ?count)
        WHERE {
          ?instance a ?type .
        }
        GROUP BY ?type
      `)
      const counts: Record<string, number> = {}
      for (const binding of result.results.bindings) {
        if (binding.type?.value && binding.count?.value) {
          counts[binding.type.value] = parseInt(binding.count.value, 10)
        }
      }
      return counts
    },
    staleTime: 60000,
  })

  const stats = useMemo(() => ({
    total: ALL_CLASSES.length,
    market: getClassesByDomain('market').length,
    business: getClassesByDomain('business').length,
    solution: getClassesByDomain('solution').length,
    totalInstances: Object.values(instanceCounts).reduce((a, b) => a + b, 0),
  }), [instanceCounts])

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '18px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              backgroundColor: 'var(--color-warm-100)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text-secondary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Layers style={{ width: '24px', height: '24px' }} />
          </div>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: 690, color: 'var(--color-text)', margin: 0, letterSpacing: '0' }}>
              Ontology Explorer
            </h1>
            <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', margin: 0 }}>
              {stats.total} classes • {stats.totalInstances} instances across 3 layers
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Educational Mode Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowEducational(!showEducational)}
            style={{
              backgroundColor: showEducational ? 'var(--color-market-50)' : 'var(--color-surface)',
              borderColor: showEducational ? 'var(--color-market-100)' : 'var(--color-border)',
              color: showEducational ? 'var(--color-market-700)' : 'var(--color-text-secondary)',
              boxShadow: 'none',
            }}
          >
            <BookOpen style={{ width: '14px', height: '14px', marginRight: '6px' }} />
            Learn Mode
          </Button>

          {/* View Mode Selector */}
          <div
            style={{
              display: 'flex',
              backgroundColor: 'var(--color-surface)',
              borderRadius: '8px',
              padding: '4px',
              border: '1px solid var(--color-border)',
            }}
          >
            <button
              onClick={() => setViewMode('diagram')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: viewMode === 'diagram' ? 'var(--color-warm-100)' : 'transparent',
                color: viewMode === 'diagram' ? 'var(--color-text)' : 'var(--color-text-muted)',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500,
                boxShadow: viewMode === 'diagram' ? '0 1px 2px rgba(13,23,28,0.04)' : 'none',
              }}
            >
              <Network style={{ width: '14px', height: '14px' }} />
              Diagram
            </button>
            <button
              onClick={() => setViewMode('tree')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: viewMode === 'tree' ? 'var(--color-warm-100)' : 'transparent',
                color: viewMode === 'tree' ? 'var(--color-text)' : 'var(--color-text-muted)',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500,
                boxShadow: viewMode === 'tree' ? '0 1px 2px rgba(13,23,28,0.04)' : 'none',
              }}
            >
              <List style={{ width: '14px', height: '14px' }} />
              Tree
            </button>
          </div>
        </div>
      </div>

      {/* Educational Banner */}
      {showEducational && (
        <Card style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', boxShadow: 'none', flexShrink: 0 }}>
          <CardContent style={{ padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--color-market-50)',
                  color: 'var(--color-market-700)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Lightbulb style={{ width: '16px', height: '16px' }} />
              </div>
              <h3 style={{ fontSize: '14px', fontWeight: 620, color: 'var(--color-text)', margin: 0 }}>
                Three layers
              </h3>
              <div>
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                  {(['market', 'business', 'solution'] as OntologyDomain[]).map((domain) => {
                    const config = DOMAIN_CONFIG[domain]
                    return (
                      <div key={domain} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                          style={{
                            width: '7px',
                            height: '7px',
                            borderRadius: '999px',
                            backgroundColor: config.bg,
                            display: 'inline-block',
                          }}
                        />
                        <div>
                          <span style={{ fontSize: '12px', fontWeight: 560, color: 'var(--color-text-secondary)' }}>{config.label.replace(' Layer', '')}</span>
                          <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginLeft: '6px' }}>
                            {config.question.toLowerCase()}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', gap: '20px', minHeight: 0 }}>
        {/* Left: Diagram or Tree */}
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {viewMode === 'diagram' ? (
            <LayerDiagram
              selectedClass={selectedClass}
              onSelectClass={setSelectedClass}
              instanceCounts={instanceCounts}
            />
          ) : (
            <TreeView
              selectedClass={selectedClass}
              onSelectClass={setSelectedClass}
              instanceCounts={instanceCounts}
            />
          )}
        </div>

        {/* Right: Detail Panel */}
        <div style={{ width: '420px', flexShrink: 0, minWidth: 0 }}>
          {selectedClass ? (
            <ClassDetailPanel
              ontologyClass={selectedClass}
              instanceCount={instanceCounts[selectedClass.uri] || 0}
              showEducational={showEducational}
            />
          ) : showEducational ? (
            <LearnModeOverview />
          ) : (
            <Card style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center', maxWidth: '280px', padding: '40px 20px' }}>
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '16px',
                    backgroundColor: 'var(--color-warm-100)',
                    border: '1px solid var(--color-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                  }}
                >
                  <Sparkles style={{ width: '28px', height: '28px', color: 'var(--color-text-secondary)' }} />
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-text)', marginBottom: '8px' }}>
                  Select a Class
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
                  Click on any class in the diagram to see its details, properties, and educational content.
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================
// Layer Diagram View
// ============================================

interface LayerDiagramProps {
  selectedClass: OntologyClass | null
  onSelectClass: (cls: OntologyClass) => void
  instanceCounts: Record<string, number>
}

function LayerDiagram({ selectedClass, onSelectClass, instanceCounts }: LayerDiagramProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupKey)) {
        next.delete(groupKey)
      } else {
        next.add(groupKey)
      }
      return next
    })
  }

  return (
    <Card style={{ height: '100%', overflow: 'auto', border: '1px solid var(--color-border)', boxShadow: 'none' }}>
      <CardContent style={{ padding: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {(['market', 'business', 'solution'] as OntologyDomain[]).map((domain, index) => {
            const config = DOMAIN_CONFIG[domain]
            const groups = getGroupsForDomain(domain)
            const totalClasses = getClassesByDomain(domain).length
            const Icon = config.icon

            return (
              <div key={domain}>
                {/* Layer Card */}
                <div
                  style={{
                    border: `1px solid ${config.border}`,
                    borderRadius: '12px',
                    backgroundColor: 'var(--color-surface)',
                    overflow: 'hidden',
                    boxShadow: 'none',
                  }}
                >
                  {/* Layer Header */}
                  <div
                    style={{
                      padding: '14px 16px 14px 18px',
                      backgroundColor: config.lightBg,
                      borderBottom: `1px solid ${config.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <div
                      style={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '999px',
                        backgroundColor: config.bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Icon style={{ width: '13px', height: '13px', color: 'var(--color-fg-inverse)' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '15px', fontWeight: 680, color: 'var(--color-text)' }}>
                        {config.label}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', letterSpacing: '0.02em', textTransform: 'uppercase', marginTop: '3px' }}>
                        {config.question}
                      </div>
                    </div>
                    <Badge
                      style={{
                        backgroundColor: 'var(--color-surface)',
                        color: 'var(--color-text-secondary)',
                        border: '1px solid var(--color-border)',
                        fontSize: '11px',
                        fontWeight: 520,
                      }}
                    >
                      {totalClasses} classes • {groups.length} groups
                    </Badge>
                  </div>

                  {/* Groups */}
                  <div style={{ padding: '12px', backgroundColor: 'var(--color-warm-100)' }}>
                    {groups.map((group) => {
                      const groupKey = `${domain}-${group.id}`
                      const isExpanded = expandedGroups.has(groupKey)
                      const groupClasses = getClassesByGroup(domain, group.id)
                      const groupInstanceCount = groupClasses.reduce(
                        (sum, cls) => sum + (instanceCounts[cls.uri] || 0),
                        0
                      )

                      return (
                        <div key={group.id} style={{ marginBottom: '8px' }}>
                          {/* Group Header */}
                          <button
                            onClick={() => toggleGroup(groupKey)}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 12px',
                              backgroundColor: 'var(--color-surface)',
                              border: '1px solid var(--color-border)',
                              borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            <ChevronRight
                              style={{
                                width: '14px',
                                height: '14px',
                                color: config.color,
                                transition: 'transform 0.2s',
                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                              }}
                            />
                            <span style={{ fontSize: '12px', fontWeight: 560, color: 'var(--color-text-secondary)', flex: 1 }}>
                              {group.label}
                            </span>
                            <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                              {groupClasses.length} classes
                              {groupInstanceCount > 0 && ` • ${groupInstanceCount} instances`}
                            </span>
                          </button>

                          {/* Group Classes */}
                          {isExpanded && (
                            <div
                              style={{
                                padding: '12px',
                                backgroundColor: 'var(--color-warm-100)',
                                border: '1px solid var(--color-border)',
                                borderTop: 'none',
                                borderRadius: '0 0 8px 8px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px',
                              }}
                            >
                              {groupClasses.map((cls) => {
                                const count = instanceCounts[cls.uri] || 0
                                const isSelected = selectedClass?.uri === cls.uri

                                return (
                                  <button
                                    key={cls.uri}
                                    onClick={() => onSelectClass(cls)}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      padding: '6px 10px',
                                      backgroundColor: isSelected ? config.lightBg : 'var(--color-surface)',
                                      color: 'var(--color-text)',
                                      border: isSelected ? `1px solid ${config.border}` : '1px solid var(--color-border-strong)',
                                      borderRadius: '7px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      fontWeight: 400,
                                      transition: 'all 0.15s',
                                      boxShadow: 'none',
                                    }}
                                  >
                                    <span
                                      style={{
                                        width: '5px',
                                        height: '5px',
                                        borderRadius: '999px',
                                        backgroundColor: config.bg,
                                      }}
                                    />
                                    <span style={{ fontFamily: 'monospace' }}>{cls.localName}</span>
                                    {count > 0 && (
                                      <span
                                        style={{
                                          padding: '1px 5px',
                                          borderRadius: '8px',
                                          backgroundColor: config.lightBg,
                                          color: config.color,
                                          fontSize: '9px',
                                          fontWeight: 600,
                                        }}
                                      >
                                        {count}
                                      </span>
                                    )}
                                  </button>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Relationship Arrows (between layers) */}
                {index < 2 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      padding: '8px 0',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <div
                        style={{
                          width: '2px',
                          height: '16px',
                          background: `linear-gradient(to bottom, ${DOMAIN_CONFIG[domain].color}, ${DOMAIN_CONFIG[(['market', 'business', 'solution'] as OntologyDomain[])[index + 1]].color})`,
                        }}
                      />
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          backgroundColor: 'var(--color-background)',
                          borderRadius: '12px',
                          fontSize: '10px',
                          color: 'var(--color-text-muted)',
                          border: '1px solid var(--color-border)',
                        }}
                      >
                        <ArrowRight style={{ width: '10px', height: '10px' }} />
                        {index === 0 ? 'targets • solves' : 'implements • realizes'}
                      </div>
                      <div
                        style={{
                          width: '2px',
                          height: '16px',
                          background: `linear-gradient(to bottom, ${DOMAIN_CONFIG[(['market', 'business', 'solution'] as OntologyDomain[])[index + 1]].color}, ${DOMAIN_CONFIG[(['market', 'business', 'solution'] as OntologyDomain[])[index + 1]].color})`,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================
// Tree View
// ============================================

interface TreeViewProps {
  selectedClass: OntologyClass | null
  onSelectClass: (cls: OntologyClass) => void
  instanceCounts: Record<string, number>
}

function TreeView({ selectedClass, onSelectClass, instanceCounts }: TreeViewProps) {
  const [expandedDomains, setExpandedDomains] = useState<Set<OntologyDomain>>(
    new Set(['market', 'business', 'solution'])
  )
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

  const toggleDomain = (domain: OntologyDomain) => {
    setExpandedDomains((prev) => {
      const next = new Set(prev)
      if (next.has(domain)) {
        next.delete(domain)
      } else {
        next.add(domain)
      }
      return next
    })
  }

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupKey)) {
        next.delete(groupKey)
      } else {
        next.add(groupKey)
      }
      return next
    })
  }

  return (
    <Card style={{ height: '100%', overflow: 'auto' }}>
      <CardContent style={{ padding: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {(['market', 'business', 'solution'] as OntologyDomain[]).map((domain) => {
            const config = DOMAIN_CONFIG[domain]
            const classes = getClassesByDomain(domain)
            const groups = getGroupsForDomain(domain)
            const isExpanded = expandedDomains.has(domain)
            const totalInstances = classes.reduce((sum, cls) => sum + (instanceCounts[cls.uri] || 0), 0)

            return (
              <div key={domain}>
                {/* Domain Header */}
                <button
                  onClick={() => toggleDomain(domain)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    backgroundColor: config.lightBg,
                    border: `1px solid ${config.border}`,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <ChevronRight
                    style={{
                      width: '16px',
                      height: '16px',
                      color: config.color,
                      transition: 'transform 0.2s',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  />
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: config.bg,
                    }}
                  />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: config.color, flex: 1 }}>
                    {config.label}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                    {classes.length} classes • {totalInstances} instances
                  </span>
                </button>

                {/* Groups */}
                {isExpanded && (
                  <div style={{ paddingLeft: '20px', marginTop: '8px' }}>
                    {groups.map((group) => {
                      const groupKey = `${domain}-${group.id}`
                      const isGroupExpanded = expandedGroups.has(groupKey)
                      const groupClasses = getClassesByGroup(domain, group.id)
                      const groupInstanceCount = groupClasses.reduce(
                        (sum, cls) => sum + (instanceCounts[cls.uri] || 0),
                        0
                      )

                      return (
                        <div key={group.id} style={{ marginBottom: '4px' }}>
                          {/* Group Header */}
                          <button
                            onClick={() => toggleGroup(groupKey)}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '6px 10px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            <ChevronRight
                              style={{
                                width: '12px',
                                height: '12px',
                                color: 'var(--color-text-muted)',
                                transition: 'transform 0.2s',
                                transform: isGroupExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                              }}
                            />
                            <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--color-text)', flex: 1 }}>
                              {group.label}
                            </span>
                            <span style={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>
                              {groupClasses.length}
                              {groupInstanceCount > 0 && ` • ${groupInstanceCount}`}
                            </span>
                          </button>

                          {/* Class List */}
                          {isGroupExpanded && (
                            <div style={{ paddingLeft: '20px' }}>
                              {groupClasses.map((cls) => {
                                const count = instanceCounts[cls.uri] || 0
                                const isSelected = selectedClass?.uri === cls.uri

                                return (
                                  <button
                                    key={cls.uri}
                                    onClick={() => onSelectClass(cls)}
                                    style={{
                                      width: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      padding: '5px 10px',
                                      backgroundColor: isSelected ? config.lightBg : 'transparent',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      textAlign: 'left',
                                      border: isSelected ? `1px solid ${config.border}` : '1px solid transparent',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '5px',
                                        height: '5px',
                                        borderRadius: '50%',
                                        backgroundColor: config.bg,
                                        opacity: 0.5,
                                      }}
                                    />
                                    <span
                                      style={{
                                        fontSize: '12px',
                                        fontFamily: 'monospace',
                                        color: isSelected ? config.color : 'var(--color-text)',
                                        fontWeight: isSelected ? 500 : 400,
                                        flex: 1,
                                      }}
                                    >
                                      {cls.localName}
                                    </span>
                                    {count > 0 && (
                                      <Badge
                                        style={{
                                          backgroundColor: config.lightBg,
                                          color: config.color,
                                          fontSize: '9px',
                                          padding: '1px 5px',
                                        }}
                                      >
                                        {count}
                                      </Badge>
                                    )}
                                  </button>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================
// Enhanced Class Detail Panel
// ============================================

interface ClassDetailPanelProps {
  ontologyClass: OntologyClass
  instanceCount: number
  showEducational: boolean
}

function ClassDetailPanel({ ontologyClass, instanceCount, showEducational }: ClassDetailPanelProps) {
  const config = DOMAIN_CONFIG[ontologyClass.domain]
  const objectProperties = ontologyClass.properties.filter((p) => p.type === 'object')
  const dataProperties = ontologyClass.properties.filter((p) => p.type === 'data')

  return (
    <Card
      style={{
        height: '100%',
        overflow: 'auto',
        overflowX: 'hidden',
        border: `1px solid ${config.border}`,
        boxShadow: 'none',
      }}
    >
      <CardContent style={{ padding: '22px' }}>
        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--color-text)', margin: 0, lineHeight: 1.15, letterSpacing: 0 }}>
                {ontologyClass.localName}
              </h2>
              <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', fontFamily: 'monospace', marginTop: '8px', overflowWrap: 'anywhere', lineHeight: 1.35 }}>
                {ontologyClass.uri}
              </p>
            </div>
            <Badge style={{ backgroundColor: config.lightBg, color: config.color }}>
              {ontologyClass.domain}
            </Badge>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
            {ontologyClass.description}
          </p>
          {instanceCount > 0 && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '8px',
                padding: '4px 10px',
                backgroundColor: config.lightBg,
                borderRadius: '6px',
                fontSize: '12px',
                color: config.color,
                fontWeight: 500,
              }}
            >
              <span>{instanceCount} instances</span>
            </div>
          )}
        </div>

        {/* Educational Content */}
        {showEducational && ontologyClass.educational && (
          <div
            style={{
              marginBottom: '20px',
            }}
          >
            {/* What is this? */}
            <div style={{ paddingTop: '18px', borderTop: '1px solid var(--color-border)', marginBottom: '18px' }}>
              <h4 style={{ fontSize: '11px', fontWeight: 680, color: config.color, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                What is this?
              </h4>
              <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', lineHeight: 1.5, margin: 0 }}>
                {ontologyClass.educational.whatIsThis}
              </p>
            </div>

            {/* When to Use */}
            <div style={{ paddingTop: '18px', borderTop: '1px solid var(--color-border)', marginBottom: '18px' }}>
              <h4 style={{ fontSize: '11px', fontWeight: 680, color: config.color, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                When to use
              </h4>
              <ul style={{ margin: 0, paddingLeft: '17px' }}>
                {ontologyClass.educational.whenToUse.map((item, i) => (
                  <li key={i} style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '6px' }}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Examples */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', paddingTop: '18px', borderTop: '1px solid var(--color-border)' }}>
              <div>
                <h4 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <CheckCircle2 style={{ width: '12px', height: '12px', color: 'var(--color-success)' }} />
                  Good examples
                </h4>
                {ontologyClass.educational.goodExamples.map((example, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: '12px',
                      color: 'var(--color-text-secondary)',
                      padding: '9px 10px',
                      backgroundColor: 'var(--color-success-bg)',
                      border: '1px solid color-mix(in oklab, var(--color-success) 20%, var(--color-border))',
                      borderRadius: '7px',
                      marginBottom: '7px',
                      lineHeight: 1.35,
                    }}
                  >
                    {example}
                  </div>
                ))}
              </div>
              <div>
                <h4 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <XCircle style={{ width: '12px', height: '12px', color: 'var(--color-danger)' }} />
                  Bad examples
                </h4>
                {ontologyClass.educational.badExamples.map((example, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: '12px',
                      color: 'var(--color-text-secondary)',
                      padding: '9px 10px',
                      backgroundColor: 'var(--color-danger-bg)',
                      border: '1px solid color-mix(in oklab, var(--color-danger) 20%, var(--color-border))',
                      borderRadius: '7px',
                      marginBottom: '7px',
                      lineHeight: 1.35,
                    }}
                  >
                    {example}
                  </div>
                ))}
              </div>
            </div>

            {/* Discovery Phase */}
            {ontologyClass.educational.discoveryPhase && (
              <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid var(--color-border)' }}>
                <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>Discovery Phase: </span>
                <Badge style={{ backgroundColor: config.lightBg, color: config.color, fontSize: '10px' }}>
                  {ontologyClass.educational.discoveryPhase}
                </Badge>
                {ontologyClass.educational.discoveryRequired && (
                  <Badge style={{ backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-danger-fg)', fontSize: '10px', marginLeft: '6px' }}>
                    Required
                  </Badge>
                )}
              </div>
            )}
          </div>
        )}

        {/* Properties */}
        {objectProperties.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <h4 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Link2 style={{ width: '14px', height: '14px', color: config.color }} />
              Object Properties ({objectProperties.length})
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {objectProperties.map((prop) => (
                <div
                  key={prop.uri}
                  style={{
                    padding: '8px 10px',
                    backgroundColor: 'var(--color-background)',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                >
                  <div style={{ fontFamily: 'monospace', fontWeight: 500, color: 'var(--color-text)' }}>
                    {prop.localName}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', fontSize: '11px', color: 'var(--color-text-muted)' }}>
                    <span style={{ fontFamily: 'monospace' }}>{getLocalName(prop.domain)}</span>
                    <ArrowRight style={{ width: '10px', height: '10px' }} />
                    <span style={{ fontFamily: 'monospace', color: config.color }}>{getLocalName(prop.range)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {dataProperties.length > 0 && (
          <div>
            <h4 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text)', marginBottom: '8px' }}>
              Data Properties ({dataProperties.length})
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {dataProperties.map((prop) => (
                <div
                  key={prop.uri}
                  style={{
                    padding: '8px 10px',
                    backgroundColor: 'var(--color-background)',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 500, color: 'var(--color-text)' }}>
                      {prop.localName}
                    </span>
                    <Badge variant="outline" style={{ fontSize: '10px' }}>
                      {getLocalName(prop.range)}
                    </Badge>
                  </div>
                  {prop.description && (
                    <div style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                      {prop.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {ontologyClass.properties.length === 0 && (
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
            No properties defined for this class.
          </p>
        )}

        {/* Traceability Section */}
        {showEducational && (
          <ClassTraceabilitySection ontologyClass={ontologyClass} />
        )}

        {/* SWRL Rules Section */}
        {showEducational && (
          <ClassSWRLSection ontologyClass={ontologyClass} />
        )}
      </CardContent>
    </Card>
  )
}

// ============================================
// Class Traceability Section
// ============================================

function ClassTraceabilitySection({ ontologyClass }: { ontologyClass: OntologyClass }) {
  const config = DOMAIN_CONFIG[ontologyClass.domain]

  // Find traceability chains that involve this class
  const relevantChains = useMemo(() => {
    return TRACEABILITY_CHAINS.filter(chain =>
      chain.steps.some(step =>
        step.from === ontologyClass.localName || step.to === ontologyClass.localName
      )
    )
  }, [ontologyClass.localName])

  // Find cross-layer relationships involving this class
  const relevantRelationships = useMemo(() => {
    return CROSS_LAYER_RELATIONSHIPS.filter(rel =>
      rel.from.className === ontologyClass.localName || rel.to.className === ontologyClass.localName
    )
  }, [ontologyClass.localName])

  if (relevantChains.length === 0 && relevantRelationships.length === 0) {
    return null
  }

  return (
    <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid var(--color-border)' }}>
      <h4 style={{
        fontSize: '13px',
        fontWeight: 600,
        color: 'var(--color-text)',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <Route style={{ width: '16px', height: '16px', color: '#4a90a4' }} />
        Traceability
      </h4>

      {/* Cross-layer relationships */}
      {relevantRelationships.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Cross-Layer Connections
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {relevantRelationships.map((rel, i) => {
              const isSource = rel.from.className === ontologyClass.localName
              const otherClass = isSource ? rel.to : rel.from
              const otherConfig = DOMAIN_CONFIG[otherClass.domain]

              return (
                <div
                  key={i}
                  style={{
                    padding: '10px 12px',
                    backgroundColor: 'var(--color-background)',
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                    <span style={{ fontFamily: 'monospace', color: config.color, fontWeight: 600 }}>
                      {ontologyClass.localName}
                    </span>
                    <ArrowRightLeft style={{ width: '12px', height: '12px', color: 'var(--color-text-muted)' }} />
                    <span style={{ fontFamily: 'monospace', color: otherConfig.color, fontWeight: 600 }}>
                      {otherClass.className}
                    </span>
                    {rel.traceabilityPhase && (
                      <Badge style={{ backgroundColor: '#4a90a415', color: '#4a90a4', fontSize: '9px', marginLeft: 'auto' }}>
                        {rel.traceabilityPhase}
                      </Badge>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                    <span style={{ fontFamily: 'monospace', color: 'var(--color-text-secondary)' }}>{rel.relationship}</span>
                    {' — '}{rel.description}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Traceability chains */}
      {relevantChains.length > 0 && (
        <div>
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Part of Traceability Chains
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {relevantChains.map((chain, i) => (
              <div
                key={i}
                style={{
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <GitBranch style={{ width: '14px', height: '14px', color: '#00b894' }} />
                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text)' }}>
                    {chain.name}
                  </span>
                  <Badge style={{ backgroundColor: '#00b89415', color: '#00b894', fontSize: '9px' }}>
                    {chain.phase}
                  </Badge>
                </div>
                <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginBottom: '8px' }}>
                  {chain.description}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
                  {chain.steps.map((step, j) => (
                    <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span
                        style={{
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontFamily: 'monospace',
                          backgroundColor: step.from === ontologyClass.localName || step.to === ontologyClass.localName
                            ? config.bg
                            : 'var(--color-background)',
                          color: step.from === ontologyClass.localName || step.to === ontologyClass.localName
                            ? 'white'
                            : 'var(--color-text)',
                          fontWeight: step.from === ontologyClass.localName || step.to === ontologyClass.localName ? 600 : 400,
                        }}
                      >
                        {step.from}
                      </span>
                      <ArrowRight style={{ width: '10px', height: '10px', color: 'var(--color-text-muted)' }} />
                      {j === chain.steps.length - 1 && (
                        <span
                          style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontFamily: 'monospace',
                            backgroundColor: step.to === ontologyClass.localName
                              ? config.bg
                              : 'var(--color-background)',
                            color: step.to === ontologyClass.localName
                              ? 'white'
                              : 'var(--color-text)',
                            fontWeight: step.to === ontologyClass.localName ? 600 : 400,
                          }}
                        >
                          {step.to}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================
// Class SWRL Section
// ============================================

function ClassSWRLSection({ ontologyClass }: { ontologyClass: OntologyClass }) {
  // Find SWRL rules that mention this class
  const relevantRules = useMemo(() => {
    const className = ontologyClass.localName
    return SWRL_RULES.filter(rule =>
      rule.antecedent.includes(className) ||
      rule.consequent.includes(className) ||
      rule.swrlSyntax.includes(className)
    )
  }, [ontologyClass.localName])

  if (relevantRules.length === 0) {
    return null
  }

  const categoryIcons: Record<SWRLRule['category'], typeof ShieldCheck> = {
    validation: ShieldCheck,
    inference: Brain,
    classification: Tag,
    propagation: Share2,
  }

  const categoryColors: Record<SWRLRule['category'], string> = {
    validation: '#dc3545',
    inference: '#00b894',
    classification: '#d4a020',
    propagation: '#4a90a4',
  }

  return (
    <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid var(--color-border)' }}>
      <h4 style={{
        fontSize: '13px',
        fontWeight: 600,
        color: 'var(--color-text)',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <Zap style={{ width: '16px', height: '16px', color: '#d4a020' }} />
        Inference Rules
      </h4>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {relevantRules.map((rule, i) => {
          const Icon = categoryIcons[rule.category]
          const color = categoryColors[rule.category]

          return (
            <div
              key={i}
              style={{
                padding: '12px',
                backgroundColor: 'var(--color-background)',
                borderRadius: '8px',
                border: '1px solid var(--color-border)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    backgroundColor: `${color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon style={{ width: '14px', height: '14px', color }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text)' }}>
                      {rule.name}
                    </span>
                    <Badge style={{ backgroundColor: `${color}15`, color, fontSize: '9px', textTransform: 'capitalize' }}>
                      {rule.category}
                    </Badge>
                  </div>
                  <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', margin: 0, marginBottom: '8px' }}>
                    {rule.description}
                  </p>

                  {/* Rule Logic */}
                  <div style={{
                    padding: '8px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    fontSize: '10px',
                    fontFamily: 'monospace',
                  }}>
                    <div style={{ color: 'var(--color-text-muted)', marginBottom: '2px' }}>IF:</div>
                    <div style={{ color: 'var(--color-text)', marginBottom: '6px', paddingLeft: '8px' }}>
                      {rule.antecedent}
                    </div>
                    <div style={{ color: 'var(--color-text-muted)', marginBottom: '2px' }}>THEN:</div>
                    <div style={{ color, paddingLeft: '8px', fontWeight: 600 }}>
                      {rule.consequent}
                    </div>
                  </div>

                  {rule.example && (
                    <div style={{
                      marginTop: '8px',
                      padding: '6px 8px',
                      backgroundColor: '#00b89410',
                      borderRadius: '4px',
                      fontSize: '10px',
                      color: '#00b894',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '6px',
                    }}>
                      <Lightbulb style={{ width: '12px', height: '12px', flexShrink: 0, marginTop: '1px' }} />
                      <span>{rule.example}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ============================================
// Learn Mode Overview (when no class selected)
// ============================================

function LearnModeOverview() {
  const [activeTab, setActiveTab] = useState<'traceability' | 'swrl'>('traceability')
  const [expandedChain, setExpandedChain] = useState<number | null>(0)
  const [expandedCategory, setExpandedCategory] = useState<SWRLRule['category'] | null>('validation')

  const rulesByCategory = useMemo(() => {
    const grouped: Record<SWRLRule['category'], SWRLRule[]> = {
      validation: [],
      inference: [],
      classification: [],
      propagation: [],
    }
    SWRL_RULES.forEach(rule => {
      grouped[rule.category].push(rule)
    })
    return grouped
  }, [])

  const categoryInfo: Record<SWRLRule['category'], { icon: typeof ShieldCheck; color: string; description: string }> = {
    validation: {
      icon: ShieldCheck,
      color: '#dc3545',
      description: 'Ensure data completeness and integrity',
    },
    inference: {
      icon: Brain,
      color: '#00b894',
      description: 'Derive new facts from existing data',
    },
    classification: {
      icon: Tag,
      color: '#d4a020',
      description: 'Categorize entities based on properties',
    },
    propagation: {
      icon: Share2,
      color: '#4a90a4',
      description: 'Propagate properties through relationships',
    },
  }

  return (
    <Card style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid var(--color-border)',
        flexShrink: 0,
      }}>
        <button
          onClick={() => setActiveTab('traceability')}
          style={{
            flex: 1,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            border: 'none',
            backgroundColor: activeTab === 'traceability' ? 'var(--color-card)' : 'transparent',
            borderBottom: activeTab === 'traceability' ? '2px solid #4a90a4' : '2px solid transparent',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: activeTab === 'traceability' ? 600 : 500,
            color: activeTab === 'traceability' ? '#4a90a4' : 'var(--color-text-muted)',
          }}
        >
          <Route style={{ width: '16px', height: '16px' }} />
          Traceability
        </button>
        <button
          onClick={() => setActiveTab('swrl')}
          style={{
            flex: 1,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            border: 'none',
            backgroundColor: activeTab === 'swrl' ? 'var(--color-card)' : 'transparent',
            borderBottom: activeTab === 'swrl' ? '2px solid #d4a020' : '2px solid transparent',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: activeTab === 'swrl' ? 600 : 500,
            color: activeTab === 'swrl' ? '#d4a020' : 'var(--color-text-muted)',
          }}
        >
          <Zap style={{ width: '16px', height: '16px' }} />
          SWRL Rules
        </button>
      </div>

      <CardContent style={{ padding: '20px', overflow: 'auto', flex: 1 }}>
        {activeTab === 'traceability' ? (
          <div>
            {/* Intro */}
            <div style={{
              padding: '16px',
              backgroundColor: '#4a90a410',
              borderRadius: '12px',
              marginBottom: '20px',
              border: '1px solid #4a90a430',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: '#4a90a4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Route style={{ width: '18px', height: '18px', color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#4a90a4', marginBottom: '4px' }}>
                    What is Traceability?
                  </h3>
                  <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', lineHeight: 1.5, margin: 0 }}>
                    Traceability shows how concepts connect across the Market → Business → Solution layers.
                    It ensures every feature traces back to a customer need and every strategy traces forward to implementation.
                  </p>
                </div>
              </div>
            </div>

            {/* Chains */}
            <h4 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Traceability Chains ({TRACEABILITY_CHAINS.length})
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {TRACEABILITY_CHAINS.map((chain, i) => {
                const isExpanded = expandedChain === i
                const phaseColors: Record<string, string> = {
                  Discovery: '#00b894',
                  Shape: '#d4a020',
                  Launch: '#4a90a4',
                }

                return (
                  <div
                    key={i}
                    style={{
                      border: '1px solid var(--color-border)',
                      borderRadius: '10px',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      onClick={() => setExpandedChain(isExpanded ? null : i)}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        border: 'none',
                        backgroundColor: isExpanded ? '#f8f9fa' : 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <ChevronRight
                        style={{
                          width: '14px',
                          height: '14px',
                          color: 'var(--color-text-muted)',
                          transition: 'transform 0.2s',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}
                      />
                      <GitBranch style={{ width: '14px', height: '14px', color: phaseColors[chain.phase] }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text)' }}>
                          {chain.name}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                          {chain.description}
                        </div>
                      </div>
                      <Badge style={{ backgroundColor: `${phaseColors[chain.phase]}15`, color: phaseColors[chain.phase], fontSize: '10px' }}>
                        {chain.phase}
                      </Badge>
                    </button>

                    {isExpanded && (
                      <div style={{ padding: '0 14px 14px', backgroundColor: '#f8f9fa' }}>
                        <div style={{
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          border: '1px solid var(--color-border)',
                        }}>
                          {chain.steps.map((step, j) => (
                            <div
                              key={j}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px',
                                padding: '8px 0',
                                borderBottom: j < chain.steps.length - 1 ? '1px dashed var(--color-border)' : 'none',
                              }}
                            >
                              <div
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '50%',
                                  backgroundColor: phaseColors[chain.phase],
                                  color: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  flexShrink: 0,
                                }}
                              >
                                {j + 1}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                  <span style={{
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    backgroundColor: 'var(--color-background)',
                                    fontSize: '11px',
                                    fontFamily: 'monospace',
                                    fontWeight: 500,
                                  }}>
                                    {step.from}
                                  </span>
                                  <ArrowRight style={{ width: '12px', height: '12px', color: 'var(--color-text-muted)' }} />
                                  <span style={{
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    backgroundColor: 'var(--color-background)',
                                    fontSize: '11px',
                                    fontFamily: 'monospace',
                                    fontWeight: 500,
                                  }}>
                                    {step.to}
                                  </span>
                                </div>
                                <div style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                                  <span style={{ fontFamily: 'monospace', color: phaseColors[chain.phase] }}>{step.relationship}</span>
                                  {' — '}{step.explanation}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <div>
            {/* Intro */}
            <div style={{
              padding: '16px',
              backgroundColor: '#d4a02010',
              borderRadius: '12px',
              marginBottom: '20px',
              border: '1px solid #d4a02030',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    backgroundColor: '#d4a020',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Zap style={{ width: '18px', height: '18px', color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#d4a020', marginBottom: '4px' }}>
                    What are SWRL Rules?
                  </h3>
                  <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', lineHeight: 1.5, margin: 0 }}>
                    SWRL (Semantic Web Rule Language) rules enable automated reasoning. They validate data completeness,
                    infer new facts, classify entities, and propagate properties across your knowledge graph.
                  </p>
                </div>
              </div>
            </div>

            {/* Categories */}
            <h4 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Rule Categories ({SWRL_RULES.length} rules)
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(Object.keys(categoryInfo) as SWRLRule['category'][]).map((category) => {
                const info = categoryInfo[category]
                const rules = rulesByCategory[category]
                const Icon = info.icon
                const isExpanded = expandedCategory === category

                return (
                  <div
                    key={category}
                    style={{
                      border: '1px solid var(--color-border)',
                      borderRadius: '10px',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      onClick={() => setExpandedCategory(isExpanded ? null : category)}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        border: 'none',
                        backgroundColor: isExpanded ? '#f8f9fa' : 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <ChevronRight
                        style={{
                          width: '14px',
                          height: '14px',
                          color: 'var(--color-text-muted)',
                          transition: 'transform 0.2s',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}
                      />
                      <div
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px',
                          backgroundColor: `${info.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon style={{ width: '14px', height: '14px', color: info.color }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text)', textTransform: 'capitalize' }}>
                          {category}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                          {info.description}
                        </div>
                      </div>
                      <Badge style={{ backgroundColor: `${info.color}15`, color: info.color, fontSize: '10px' }}>
                        {rules.length}
                      </Badge>
                    </button>

                    {isExpanded && (
                      <div style={{ padding: '0 14px 14px', backgroundColor: '#f8f9fa' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {rules.map((rule, j) => (
                            <div
                              key={j}
                              style={{
                                padding: '12px',
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                border: '1px solid var(--color-border)',
                              }}
                            >
                              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text)', marginBottom: '6px' }}>
                                {rule.name}
                              </div>
                              <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', margin: 0, marginBottom: '10px' }}>
                                {rule.description}
                              </p>

                              <div style={{
                                padding: '8px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '6px',
                                fontSize: '10px',
                                fontFamily: 'monospace',
                              }}>
                                <div style={{ color: 'var(--color-text-muted)', marginBottom: '2px' }}>IF:</div>
                                <div style={{ color: 'var(--color-text)', marginBottom: '6px', paddingLeft: '8px', wordBreak: 'break-word' }}>
                                  {rule.antecedent}
                                </div>
                                <div style={{ color: 'var(--color-text-muted)', marginBottom: '2px' }}>THEN:</div>
                                <div style={{ color: info.color, paddingLeft: '8px', fontWeight: 600 }}>
                                  {rule.consequent}
                                </div>
                              </div>

                              {rule.example && (
                                <div style={{
                                  marginTop: '8px',
                                  padding: '6px 8px',
                                  backgroundColor: '#00b89410',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  color: '#00b894',
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '6px',
                                }}>
                                  <Lightbulb style={{ width: '12px', height: '12px', flexShrink: 0, marginTop: '1px' }} />
                                  <span>{rule.example}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
